import { ReactNode } from "react";

import { useDraggable } from "@dnd-kit/core";
import { Box } from "@mui/material";

import { DocumentGroup, DocumentResponse } from "@/api";
import { DragData } from "@/common/components/layout/components/dropzone/dnd/DropZoneDnd.types";
import { useDocumentRowActivation } from "@/common/components/layout/components/dropzone/dnd/hooks/useDocumentRowActivation";
import { useTranslation } from "@/translations";

interface Props {
  file: DocumentResponse;
  /** The asset inventory group this row belongs to. */
  sourceZone: DocumentGroup;
  /** Disables dragging, e.g. while the row is marked for deletion. */
  disabled?: boolean;
  onActivate?: () => void;
  children: ReactNode;
}

/**
 * Makes a saved asset inventory file row draggable into the dropzone panel. Dropping it there
 * queues a pending move in the view's form — persisted on save, reverted on reset. The row is
 * both the draggable node and the keyboard activator, so Space on nested controls (e.g.
 * the row's action menu) does not start a drag.
 */
export function DraggableDocumentRow({ file, sourceZone, disabled = false, onActivate, children }: Props) {
  const { t } = useTranslation();
  const dragData: DragData = { file, sourceZone };
  const { setNodeRef, setActivatorNodeRef, listeners, attributes, isDragging } = useDraggable({
    id: `${sourceZone}-${file.documentId}`,
    data: dragData,
    disabled,
    attributes: { roleDescription: t("dropzone.dnd.draggableRoleDescription") },
  });
  const rowActivation = useDocumentRowActivation({ onActivate, disabled: disabled || isDragging });

  return (
    <Box
      ref={(node: HTMLElement | null) => {
        setNodeRef(node);
        setActivatorNodeRef(node);
      }}
      {...listeners}
      {...attributes}
      {...rowActivation}
      aria-label={t("dropzone.dnd.openPreview", { fileName: file.fileName })}
      sx={{
        width: "100%",
        opacity: isDragging ? 0.4 : 1,
        cursor: disabled ? undefined : "grab",
        "&:active": disabled ? undefined : { cursor: "grabbing" },
      }}
    >
      {children}
    </Box>
  );
}
